import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery } from "@apollo/client";
import { GET_CHANGELOG } from "./queries";
import {
  ChangeLog,
  SortOrders,
  TableColumn,
} from "../../components/helpers/types";
import { DateTimeCell } from "../../components/Table/DateTimeCell";
import Table from "../../components/Table/Table";
import ChangeLogDetails from "./ChangeLogDetails";
import styled from "@emotion/styled";
import TableRow, { tableRowClasses } from "@mui/material/TableRow";
import { useParams } from "react-router-dom";
import { SearchFilters } from "./ChangelogDashboard";
import { AuthContext } from "../../auth/AuthProvider";
import { AuthState } from "@nike/aegis-auth-react/dist/types/store";

// TODO: replace later with a loader indicator
const StyledTableLoader = styled(TableRow)(() => ({
  [`&.${tableRowClasses.root}`]: {
    height: "1rem",
  },
}));

interface ChangeLogProps {
  filters: SearchFilters;
}

const ChangeLogTable: FC<ChangeLogProps> = ({
  filters: { service, eventDateBefore, eventDateAfter, author },
}) => {
  const loaderRef = useRef(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const authContext = useContext(AuthContext) as AuthState;

  let { id } = useParams();
  // initial load...no anchor
  const { data, loading, fetchMore } = useQuery(GET_CHANGELOG, {
    errorPolicy: "all",
    variables: {
      id: id || "",
      service,
      eventDateBefore,
      eventDateAfter,
      author,
      anchor: "",
    },
    context: {
      headers: {
        Authorization: `Bearer ${authContext?.accessToken}`,
      },
    },
  });

  // memoize rows and anchor
  const rows = useMemo(() => data?.changelog?.objects || [], [data?.changelog]);
  const anchor = useMemo(
    () => data?.changelog?.pages?.anchor || "",
    [data?.changelog]
  );

  // fetch data based on search filters
  const fetchData = useCallback(async () => {
    if (rows.length === 0 || !anchor) return;

    await fetchMore({
      variables: {
        id,
        service,
        eventDateBefore,
        eventDateAfter,
        author,
        anchor,
      },
      // use updateQuery to append new data to the existing data
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          console.log("no more data to fetch", {
            prev,
            fetchMoreResult,
            rows,
            data,
          });
          return prev;
        } else {
          console.log("fetching more data...", {
            prev,
            fetchMoreResult,
            rows,
            data,
          });
          return {
            changelog: {
              ...fetchMoreResult.changelog,
              objects: [
                ...prev.changelog.objects,
                ...fetchMoreResult.changelog.objects,
              ],
              pages: fetchMoreResult.changelog.pages,
            },
          };
        }
      },
    });
  }, [
    anchor,
    author,
    data,
    eventDateAfter,
    eventDateBefore,
    fetchMore,
    id,
    rows,
    service,
  ]);

  // use intersection observer on table to fetch more rows if needed
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      const target = entries[0];
      if (target.isIntersecting) {
        console.log("pagination reached, fetching more data...");
        fetchData().then();
      }
    });

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current);
      }
    };
  }, [fetchData, data, anchor]);

  const columns: TableColumn<ChangeLog>[] = useMemo(
    () => [
      {
        id: "Service",
        header: "Service",
        width: 100,
        valueGetter: ({ service }) => service,
        sortable: true,
        filterable: true,
      },
      {
        id: "EventDate",
        header: "Event Date",
        width: 200,
        valueGetter: ({ eventDate }) => <DateTimeCell date={eventDate} />,
        sortable: true,
        filterable: false,
      },
      {
        id: "Action",
        header: "Action",
        width: 100,
        valueGetter: ({ action }) => action,
        sortable: true,
        filterable: true,
      },
      {
        id: "Author",
        header: "Author",
        width: 100,
        valueGetter: ({ author }) => author,
        sortable: true,
        filterable: false,
      },
      {
        id: "Notes",
        header: "Notes",
        width: 300,
        valueGetter: ({ notes }) => notes,
        sortable: true,
        filterable: false,
      },
    ],
    []
  );

  return (
    <>
      <ChangeLogDetails
        selectedRow={selectedRow}
        setSelectedRow={setSelectedRow}
      />
      <Table
        loading={loading && rows.length === 0}
        columns={columns}
        rows={rows}
        order={SortOrders.ASC}
        orderBy={"EventDate"}
        onSort={(eventDate: string) => null}
        selectedRow={selectedRow}
        setSelectedRow={setSelectedRow}
        emptyMessage={"No records: add resourceId to path"}
      />
      <StyledTableLoader ref={loaderRef} />
    </>
  );
};

export default ChangeLogTable;
